import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation } from "convex/react";
import { useQuery } from "@/lib/convex";
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  Icon,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import {
  LuCalendarDays,
  LuCircleCheckBig,
  LuMessageSquareWarning,
  LuUserCheck,
} from "react-icons/lu";
import type { FunctionReturnType } from "convex/server";
import { api } from "@convex/_generated/api";
import { readableError } from "@/lib/errors";
import { toastError, toastSuccess } from "../components/ui/toaster";
import {
  ReviewFormFields,
  missingRequiredReviewFields,
  type ReviewFieldValue,
} from "@/components/ReviewFormFields";
import type { Id } from "@convex/_generated/dataModel";
import { useEffectivePersonId } from "../impersonation";
import { LoadingCard } from "../components/LoadingCard";
import { PageHeader } from "../components/PageHeader";
import { formatDay } from "./ActivityAdminShared";

type PendingReview =
  FunctionReturnType<typeof api.reviews.listPendingForViewer>[number];

/**
 * What is waiting for this person's signature.
 *
 * The queue already tells us WHICH reviewer slot the viewer may sign for -- a
 * task can name several, and somebody can be two of them at once -- so the
 * buttons are per slot rather than per task.
 */
/**
 * The composer's values into what the mutation stores.
 *
 * Unanswered optional fields are dropped rather than stored as null: a key
 * that is absent reads as "not answered", where a null would have to be told
 * apart from a deliberate blank.
 */
function toResponseData(values: Record<string, ReviewFieldValue>) {
  return Object.fromEntries(
    Object.entries(values).filter(([, value]) =>
      value !== null && value !== undefined && String(value) !== ""),
  ) as Record<string, string | number | boolean>;
}

/**
 * The approve / request-revision form for one reviewer slot.
 *
 * Its own component because all of its state is its own: which way the
 * decision is going, the comment, and the answers to the review form. While
 * that state sat on the page, typing a single character of a comment
 * re-rendered every other row in the queue -- rows that cannot change while
 * somebody writes.
 */
function DecisionComposer({
  review,
  slot,
  onClose,
}: {
  review: PendingReview;
  slot: PendingReview["actionable"][number];
  onClose: () => void;
}) {
  const approveTask = useMutation(api.reviews.approveTask);
  const requestTaskRevision = useMutation(api.reviews.requestTaskRevision);
  const effectivePersonId = useEffectivePersonId() as Id<"persons"> | null;

  const [decision, setDecision] = useState<"approve" | "revise">("approve");
  const [comment, setComment] = useState("");
  const [reviewValues, setReviewValues] = useState<Record<string, ReviewFieldValue>>({});
  const [busy, setBusy] = useState(false);

  const reviewFields = review.reviewFormSchema?.fields ?? [];

  async function submit() {
    // A revision request without a reason is useless to whoever has to act on
    // it; an approval needs no words.
    if (decision === "revise" && comment.trim() === "") {
      toastError("Say what needs changing.");
      return;
    }

    // Only an approval carries the assessment, so only an approval has to
    // have answered it.
    if (decision === "approve" && reviewFields.length > 0) {
      const missing = missingRequiredReviewFields(reviewFields, reviewValues);
      if (missing.length > 0) {
        toastError("Still to answer", missing.join(", "));
        return;
      }
    }

    setBusy(true);
    try {
      const args = { taskId: review.taskId, reviewerSpec: slot.spec, comment: comment.trim(), effectivePersonId };
      if (decision === "approve") {
        await approveTask(
          reviewFields.length > 0
            ? { ...args, responseData: toResponseData(reviewValues) }
            : args,
        );
        toastSuccess("Approved", review.taskTitle);
      } else {
        await requestTaskRevision(args);
        toastSuccess("Revision requested", review.taskTitle);
      }
      onClose();
    } catch (error) {
      // The sentence first; the browser's own words second, where they
      // add anything. On their own they tell the reader nothing.
      const readable = readableError(error, "The decision could not be recorded");
      toastError(readable.message, readable.detail);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack gap={3}>
      <HStack gap={2}>
        <Button
          size="xs"
          variant={decision === "approve" ? "solid" : "outline"}
          colorPalette="green"
          onClick={() => setDecision("approve")}
        >
          Approve
        </Button>
        <Button
          size="xs"
          variant={decision === "revise" ? "solid" : "outline"}
          colorPalette="orange"
          onClick={() => setDecision("revise")}
        >
          Request revision
        </Button>
        <Text fontSize="xs" color="fg.muted">as {slot.label}</Text>
      </HStack>

      {decision === "approve" && reviewFields.length > 0 ? (
        <Box borderWidth="1px" borderColor="border" rounded="md" bg="bg.muted" p={4}>
          <ReviewFormFields
            fields={reviewFields}
            values={reviewValues}
            disabled={busy}
            onChange={(key, value) => setReviewValues((current) => ({ ...current, [key]: value }))}
          />
        </Box>
      ) : null}

      <Textarea
        rows={3}
        value={comment}
        placeholder={decision === "approve" ? "Optional comment" : "What needs changing?"}
        onChange={(event) => setComment(event.target.value)}
      />

      <HStack gap={3}>
        <Button
          colorPalette={decision === "approve" ? "green" : "orange"}
          loading={busy}
          onClick={() => void submit()}
        >
          {decision === "approve" ? "Approve" : "Request revision"}
        </Button>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
      </HStack>
    </Stack>
  );
}

export function ActivityReviewsPage() {
  // Follows impersonation, like the rest of the reads: looking at the app as
  // a supervisor should show that supervisor's queue.
  const effectivePersonId = useEffectivePersonId();
  const pending = useQuery(api.reviews.listPendingForViewer, {
    effectivePersonId: effectivePersonId as Id<"persons"> | null,
  });

  // One open composer at a time, keyed by task + slot: a comment belongs to
  // the decision being made, not to the page.
  const [openKey, setOpenKey] = useState<string | null>(null);


  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title="Reviews"
        description="Work waiting for your sign-off."
        breadcrumbs={[{ label: "CeMMbase", to: "/" }, { label: "Activities", to: "/activities" }, { label: "Reviews" }]}
        actions={pending && pending.length > 0
          ? (
            <Badge variant="outline" px={4} py={2} textTransform="uppercase" letterSpacing="0.2em">
              {pending.length} waiting
            </Badge>
          )
          : null}
      />

      <Stack gap={5} px={{ base: 6, sm: 8, lg: 10 }} py={{ base: 6, lg: 8 }}>

        {pending === undefined ? (
          <LoadingCard label="Loading reviews…" />
        ) : pending.length === 0 ? (
          <Card.Root borderWidth="1px" borderStyle="dashed" borderColor="border">
            <Card.Body gap={2} p={8}>
              <Heading size="md">Nothing waiting for you</Heading>
              <Text fontSize="sm" color="fg.muted">
                Work appears here when someone submits something you sign off.
              </Text>
            </Card.Body>
          </Card.Root>
        ) : (
          pending.map((review) => (
            <Card.Root key={review.taskId} borderWidth="1px" borderColor="border">
              <Card.Body gap={4} p={6}>
                <Flex direction={{ base: "column", lg: "row" }} align={{ lg: "flex-start" }} justify="space-between" gap={4}>
                  <Stack gap={2}>
                    <HStack flexWrap="wrap" gap={3}>
                      <Heading size="lg">{review.taskTitle}</Heading>
                      <Badge variant="subtle" colorPalette={review.kind === "complete" ? "purple" : "teal"}>
                        {review.kind === "complete" ? "To complete" : "To approve"}
                      </Badge>
                      {review.actionable.map((slot) => (
                        <Badge key={slot.label} variant="outline" colorPalette="blue">
                          <Icon as={LuUserCheck} boxSize={3} />
                          as {slot.label}
                        </Badge>
                      ))}
                    </HStack>
                    <Text fontSize="sm" color="fg.muted">
                      {review.personName} — {review.activityTitle}
                    </Text>
                    <HStack gap={4} fontSize="sm" color="fg.muted">
                      <HStack gap={2}>
                        <Icon as={LuCalendarDays} boxSize={4} />
                        {/* The step's own deadline where it has one, since that
                            is the date that binds and the one the queue is
                            ordered by. The activity's is named beside it, so
                            the shorter deadline does not look like the whole
                            story. */}
                        {review.taskDueDay ? (
                          <Text>
                            Step due {formatDay(review.taskDueDay)}
                            {review.dueDay ? ` · activity ${formatDay(review.dueDay)}` : ""}
                          </Text>
                        ) : (
                          <Text>Due {formatDay(review.dueDay)}</Text>
                        )}
                      </HStack>
                      <Text>{review.reviewSummaryLabel}</Text>
                    </HStack>
                  </Stack>

                  <Button asChild variant="outline" size="sm">
                    <Link to="/activities/$personActivityId" params={{ personActivityId: String(review.personActivityId) }} search={{ from: "reviews" }}>
                      Open the activity
                    </Link>
                  </Button>
                </Flex>

                {review.kind === "complete" ? (
                  <Box borderTopWidth="1px" borderColor="border" pt={4}>
                    <Text fontSize="sm" color="fg.muted">
                      You fill this one in yourself — open the activity to complete it.
                    </Text>
                  </Box>
                ) : review.actionable.map((slot, slotIndex) => {
                  const key = `${review.taskId}:${slotIndex}`;
                  return (
                    <Box key={key} borderTopWidth="1px" borderColor="border" pt={4}>
                      {openKey === key ? (
                        <DecisionComposer
                          review={review}
                          slot={slot}
                          onClose={() => setOpenKey(null)}
                        />
                      ) : (
                        <HStack gap={3}>
                          <Button size="sm" colorPalette="green" onClick={() => setOpenKey(key)}>
                            <LuCircleCheckBig />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            colorPalette="orange"
                            onClick={() => setOpenKey(key)}
                          >
                            <LuMessageSquareWarning />
                            Request revision
                          </Button>
                          {review.actionable.length > 1 ? (
                            <Text fontSize="xs" color="fg.muted">as {slot.label}</Text>
                          ) : null}
                        </HStack>
                      )}
                    </Box>
                  );
                })}
              </Card.Body>
            </Card.Root>
          ))
        )}
      </Stack>
    </Box>
  );
}
